// src/components/SkeletonScreen.js
import React from 'react';
import './SkeletonScreen.css';

/**
 * Universal content placeholder that mimics the layout of different page types
 * to reduce layout shifts during loading
 * 
 * @param {Object} props Component props
 * @param {string} props.type The type of skeleton to show (home, product, productDetail)
 * @param {number} props.count Number of items to show in a list
 * @param {boolean} props.isVisible Whether the skeleton is visible
 */
function SkeletonScreen({ type = 'generic', count = 4, isVisible = true }) {
  if (!isVisible) return null;

  // Home page skeleton
  if (type === 'home') {
    return (
      <div className="skeleton-home" aria-busy="true" aria-live="polite">
        <div className="skeleton-hero pulse"></div>
        <div className="skeleton-section pulse">
          <div className="skeleton-heading"></div>
          <div className="skeleton-product-grid">
            {Array(count).fill(0).map((_, i) => (
              <div key={i} className="skeleton-product-card">
                <div className="skeleton-image"></div>
                <div className="skeleton-text-short"></div>
                <div className="skeleton-text-medium"></div>
                <div className="skeleton-button"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Product list skeleton
  if (type === 'product') {
    return (
      <div className="skeleton-products" aria-busy="true" aria-live="polite">
        <div className="skeleton-header pulse">
          <div className="skeleton-heading"></div>
          <div className="skeleton-filters">
            <div className="skeleton-filter-item"></div>
            <div className="skeleton-filter-item"></div>
            <div className="skeleton-filter-item"></div>
          </div>
        </div>
        <div className="skeleton-product-grid">
          {Array(count).fill(0).map((_, i) => (
            <div key={i} className="skeleton-product-card pulse">
              <div className="skeleton-image"></div>
              <div className="skeleton-text-short"></div>
              <div className="skeleton-text-medium"></div>
              <div className="skeleton-button"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Product detail skeleton
  if (type === 'productDetail') {
    return (
      <div className="skeleton-product-detail" aria-busy="true" aria-live="polite">
        <div className="skeleton-breadcrumb pulse"></div>
        <div className="skeleton-detail-container">
          <div className="skeleton-image-large pulse"></div>
          <div className="skeleton-detail-info">
            <div className="skeleton-title pulse"></div>
            <div className="skeleton-price pulse"></div>
            <div className="skeleton-description pulse"></div>
            <div className="skeleton-description pulse"></div>
            <div className="skeleton-button-large pulse"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Default/generic skeleton
  return (
    <div className="skeleton-generic" aria-busy="true" aria-live="polite">
      <div className="skeleton-header pulse"></div>
      <div className="skeleton-content">
        <div className="skeleton-text pulse"></div>
        <div className="skeleton-text pulse"></div>
        <div className="skeleton-text pulse"></div>
      </div>
    </div>
  );
}

export default SkeletonScreen;